import { FC, ReactNode } from 'react';
import { UnallocatedTask } from '../../../../../types/carAllocation';
import styles from './styles.module.css';
import { format } from 'date-fns';
import CollectionConstraintTypeTag from '../../../../../components/common/CollectionConstraintTypeTag';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import WorkCategoryTag from '../../../../../components/common/WorkCategoryTag';
import Button from '../../../../../components/common/Button';
import { Link } from 'react-router-dom';
import { useOrderCalcAllocationMutation } from '../../../../../api/carAllocationApi';
import { logger } from '../../../../../services/logger';
import PolicyRequired from '../../../../../components/common/PolicyRequired';
import { CalcStatus } from '../../../../../types/contracts';
import { getWeekDaysText } from '../../../../../services/weekDays';
import { getNumberString } from '../../../../../services/getNumberString';
import { useSaveOrigin } from '../../../useSaveOrigin';

/**
 * 分(数値)を時:分(文字列)に変換する
 * @param minutes number
 * @returns string
 */
const minutesToTime = (minutes: number) => {
  const MINUTES_IN_HOUR = 60 as const;
  const hours = `${Math.floor(minutes / MINUTES_IN_HOUR)}`;
  const restMinutes = `${minutes % MINUTES_IN_HOUR}`.padStart(2, '0');
  return `${hours}:${restMinutes}`;
};

const fullDateFormatStr = 'yyyy/M/d';

/**
 * 「配車を計算」ボタンを表示する計算状態
 * @see https://fenrir-inc.backlog.com/view/MRBN_UR_INHOUSE-453#comment-320237568
 */
const showCalcButtonStatuses: CalcStatus[] = [
  'uncalced',
  'partComplete',
  'updateContract',
];

type Props = { selected: UnallocatedTask; onClose: () => void };
/**
 * 未配車詳細ポップアップ
 * 表示位置が固定のため位置調整のライブラリは使用しない
 */
const DetailPopup: FC<Props> = ({ selected, onClose }) => {
  const startDate = new Date(selected.workStartDate);
  const endDate = selected.workEndDate
    ? new Date(selected.workEndDate)
    : undefined;
  const title = `${format(startDate, fullDateFormatStr)}${
    endDate ? `-${format(endDate, fullDateFormatStr)}` : ''
  }`;
  const [order] = useOrderCalcAllocationMutation();
  const onOrder = () => {
    order().unwrap().catch(logger.error);
  };
  const linkQuery = useSaveOrigin();

  return (
    <div className={styles.container}>
      <div className={styles.headContainer}>
        <div className={styles.accentBar}></div>
        <div className={styles.head}>
          <div className={styles.leftHead}>
            <div className={styles.headText}>{title}</div>
            {selected.collectionConstraintType.id === 1 &&
              selected.collectionConstraintWeekDays &&
              selected.collectionConstraintWeekDays.length > 0 && (
                <div className={styles.weekDay}>
                  {getWeekDaysText(selected.collectionConstraintWeekDays)}
                </div>
              )}
            <CollectionConstraintTypeTag
              type={selected.collectionConstraintType}
            />
          </div>
          <button onClick={onClose} className={styles.closeButton}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      </div>
      <div className={styles.body}>
        <Section>
          <SectionHead>{selected.unallocatedReason.title}</SectionHead>
          <div className={styles.contentText}>
            {selected.unallocatedReason.message}
          </div>
          <PolicyRequired policy={29}>
            {showCalcButtonStatuses.includes(selected.calcStatus) && (
              <div className={styles.calcAllocateButtonContainer}>
                <Button onClick={onOrder} variants={['contain']}>
                  配車を計算
                </Button>
              </div>
            )}
          </PolicyRequired>
        </Section>
        <Section>
          <SectionHead>{selected.company.name}</SectionHead>
          <div className={styles.contentText}>{selected.company.address}</div>
          {selected.workCategories.map((wc) => (
            <div key={wc.id} className={styles.workCategory}>
              <WorkCategoryTag value={wc} />
              <div className={styles.estimatedTime}>
                {minutesToTime(wc.estimatedTime)}
              </div>
            </div>
          ))}
          {/* 廃棄品目は3行まで表示 */}
          {(selected.wasteItems ?? [])
            .filter((_, i) => i < 3)
            .map((item, index) => (
              <div key={item.id} className={styles.wasteItems}>
                {item.name}
                <div className={styles.splitBar}></div>
                {`${getNumberString(item.amount)}㎥${index === 2 ? '...' : ''}`}
              </div>
            ))}
          <PolicyRequired policy={18}>
            <div className={styles.openDetailContainer}>
              <Link
                to={`/contracts/edit/${selected.contractId}${linkQuery}`}
                className={styles.openDetail}
              >
                詳細を見る
              </Link>
            </div>
          </PolicyRequired>
        </Section>
      </div>
    </div>
  );
};

type SectionProps = {
  children: ReactNode;
};
const Section: FC<SectionProps> = ({ children }) => {
  return <div className={styles.section}>{children}</div>;
};
const SectionHead: FC<SectionProps> = ({ children }) => {
  return <div className={styles.sectionHead}>{children}</div>;
};

export default DetailPopup;
